import {
  QueryClient,
  useIsMutating,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import {
  deleteComment,
  fetchCommentThreads,
  fetchResolvedThreads,
  postComment,
  resolveCommentThread
} from '../api/data-qa-api';
import { settlePendingComment, withoutComment, withPendingComment } from '../comment-utils';
import {
  MMMDataQAComment,
  MMMDataQACommentKind,
  MMMDataQACommentThread,
  MMMDataQAResolvedThread,
  MMMDataQAThreadScope,
  PostMMMDataQACommentBody
} from '../data-qa-types';

export const commentThreadsQueryKey = (cid: string) => ['mmmDataQAComments', cid];

const resolvedThreadsQueryKey = (cid: string, scope?: MMMDataQAThreadScope) =>
  scope
    ? ['mmmDataQAResolvedComments', cid, scope.tab, scope.stage]
    : ['mmmDataQAResolvedComments', cid];

// While the chat is open, check for new comments every 20 seconds; while it's collapsed,
// every minute, for its unread badge and the step bar's markers. Also on window focus.
const OPEN_POLL_MS = 20_000;
const COLLAPSED_POLL_MS = 60_000;

// Every comment write (post, resolve, delete) for a client shares this key, so a refetch
// can wait until none is in flight (see refetchWhenIdle).
const commentWritesKey = (cid: string) => ['mmmDataQACommentWrites', cid];

// Refetches the threads once no other comment write is still in flight: the server's copy
// doesn't have that write's change yet, so refetching mid-write would wipe its pending
// comment (or bring back a comment being deleted) until it settles. Called from a write's
// own onSettled, where that write still counts as in flight.
const refetchWhenIdle = (queryClient: QueryClient, cid: string) => {
  if (queryClient.isMutating({ mutationKey: commentWritesKey(cid) }) <= 1) {
    queryClient.invalidateQueries({ queryKey: commentThreadsQueryKey(cid) });
  }
};

const updateThreads = (
  queryClient: QueryClient,
  cid: string,
  update: (current: MMMDataQACommentThread[]) => MMMDataQACommentThread[]
) =>
  queryClient.setQueryData<MMMDataQACommentThread[]>(commentThreadsQueryKey(cid), prev =>
    update(prev ?? [])
  );

type PostVariables = Omit<PostMMMDataQACommentBody, 'cid'>;

// Pending comments' temporary ids: negative, so they can't collide with a server id, and
// unique for the page's lifetime.
let lastPendingId = 0;
const nextPendingId = (): number => {
  lastPendingId -= 1;
  return lastPendingId;
};

// Posting shows the comment at once, as pending, until the server's copy replaces it; a
// failed post takes it back out. Exported for unit testing.
export const postCommentOptions = (
  queryClient: QueryClient,
  cid: string,
  userEmail: string
): UseMutationOptions<MMMDataQAComment, Error, PostVariables, { pendingId: number }> => ({
  mutationKey: commentWritesKey(cid),
  mutationFn: body => postComment({ ...body, cid }),
  onMutate: async ({ modelKey, tab, stage, body, kind }) => {
    // A refetch already under way would land without the pending comment.
    await queryClient.cancelQueries({ queryKey: commentThreadsQueryKey(cid) });
    const pending: MMMDataQAComment = {
      id: nextPendingId(),
      threadId: 0,
      authorEmail: userEmail,
      authorName: null,
      kind: kind ?? MMMDataQACommentKind.COMMENT,
      body,
      createdAt: new Date().toISOString()
    };
    updateThreads(queryClient, cid, current =>
      withPendingComment(current, { modelKey, tab, stage }, pending)
    );
    return { pendingId: pending.id };
  },
  onSuccess: (saved, _body, context) => {
    if (context) {
      updateThreads(queryClient, cid, current =>
        settlePendingComment(current, context.pendingId, saved)
      );
    }
  },
  onError: (_error, _body, context) => {
    if (context) {
      updateThreads(queryClient, cid, current => settlePendingComment(current, context.pendingId));
    }
  },
  onSettled: () => refetchWhenIdle(queryClient, cid)
});

// Deleting removes the comment at once. A failed delete just refetches (once no other write
// is in flight): the server still has the comment, so it comes back without undoing
// anything else that changed meanwhile. Exported for unit testing.
export const deleteCommentOptions = (
  queryClient: QueryClient,
  cid: string
): UseMutationOptions<void, Error, number> => ({
  mutationKey: commentWritesKey(cid),
  mutationFn: commentId => deleteComment({ cid, commentId }),
  onMutate: async commentId => {
    await queryClient.cancelQueries({ queryKey: commentThreadsQueryKey(cid) });
    updateThreads(queryClient, cid, current => withoutComment(current, commentId));
  },
  onSettled: () => refetchWhenIdle(queryClient, cid)
});

// The client's open comment threads, across its models, and the actions on them. Posting
// and deleting show at once (each comment endpoint can take a few seconds on a cold start;
// see postCommentOptions / deleteCommentOptions). Polling pauses while a write is in flight,
// for the same reason as refetchWhenIdle.
// `enabled`: only internal users see comments, so only they fetch them.
export const useCommentThreads = (
  cid: string,
  userEmail: string,
  chatOpen: boolean,
  enabled: boolean
) => {
  const queryClient = useQueryClient();
  const writing = useIsMutating({ mutationKey: commentWritesKey(cid) }) > 0;
  const pollMs = chatOpen ? OPEN_POLL_MS : COLLAPSED_POLL_MS;
  const threads = useQuery<MMMDataQACommentThread[]>({
    queryKey: commentThreadsQueryKey(cid),
    queryFn: () => fetchCommentThreads(cid),
    enabled: enabled && Boolean(cid),
    refetchInterval: writing ? false : pollMs,
    refetchOnWindowFocus: !writing
  });

  const post = useMutation(postCommentOptions(queryClient, cid, userEmail));
  const resolve = useMutation({
    mutationKey: commentWritesKey(cid),
    mutationFn: ({ threadId, note }: { threadId: number; note: string }) =>
      resolveCommentThread({ cid, threadId, note }),
    onSettled: () => {
      refetchWhenIdle(queryClient, cid);
      queryClient.invalidateQueries({ queryKey: resolvedThreadsQueryKey(cid) });
    }
  });
  const remove = useMutation(deleteCommentOptions(queryClient, cid));

  return { threads, post, resolve, remove };
};

// One tab's resolved threads across the client's models, fetched only while its history
// is open; the caller keeps the models whose data the tab shares. No polling: a thread
// resolved meanwhile shows up the next time the history opens.
export const useResolvedThreads = (cid: string, scope: MMMDataQAThreadScope, enabled: boolean) =>
  useQuery<MMMDataQAResolvedThread[]>({
    queryKey: resolvedThreadsQueryKey(cid, scope),
    queryFn: () => fetchResolvedThreads(cid, scope),
    enabled: enabled && Boolean(cid),
    staleTime: 0
  });
